import { ComponentType } from 'react';
import InternshipDashboard from '../pages/InternshipDashboard';
import PerformanceReviewDashboard from '../components/PerformanceReview/PerformanceReviewDashboard';

export interface FocusTypeConfig {
  value: string;
  label: string;
  description: string;
  duration: string;
  icon: string;
  dashboardComponent: ComponentType<any>;
  setupFields: FocusSetupField[];
  supportsRubric: boolean;
  defaultDurationDays: number;
  isActive: boolean;
  quickTips: string[];
}

export interface FocusSetupField {
  name: string;
  type: 'text' | 'date' | 'text_array' | 'select';
  label: string;
  required: boolean;
  placeholder?: string;
  max?: number;
  options?: { value: string; label: string }[];
}

// Configuration for all focus types (only internship is active for MVP)
export const FOCUS_TYPES: Record<string, FocusTypeConfig> = {
  internship: {
    value: 'internship',
    label: 'Internship',
    description: 'Track your internship journey, assess your progress, and build a portfolio of accomplishments',
    duration: '10-12 weeks',
    icon: '🎯',
    dashboardComponent: InternshipDashboard,
    setupFields: [
      {
        name: 'company',
        type: 'text',
        label: 'Company',
        required: true,
        placeholder: 'e.g., Google'
      },
      {
        name: 'role',
        type: 'text',
        label: 'Role',
        required: true,
        placeholder: 'e.g., Software Engineer Intern'
      },
      {
        name: 'start_date',
        type: 'date',
        label: 'Start Date',
        required: true
      },
      {
        name: 'end_date',
        type: 'date',
        label: 'End Date',
        required: true
      },
      {
        name: 'goals',
        type: 'text_array',
        label: 'Personal Goals (up to 5)',
        required: false,
        max: 5,
        placeholder: 'What do you want to achieve during this internship?'
      }
    ],
    supportsRubric: true,
    defaultDurationDays: 84, // 12 weeks
    isActive: true,
    quickTips: [
      'Document your achievements weekly',
      'Connect with team members regularly',
      'Ask for feedback early and often',
      'Build a portfolio of your work'
    ]
  },
  
  performance_review: {
    value: 'performance_review',
    label: 'Performance Review',
    description: 'Track your performance reviews and career development with structured competency assessment',
    duration: '6-12 months',
    icon: '📊',
    dashboardComponent: PerformanceReviewDashboard,
    setupFields: [
      {
        name: 'review_cycle',
        type: 'text',
        label: 'Review Cycle',
        required: true,
        placeholder: 'e.g., 2024 Annual Review'
      },
      {
        name: 'company',
        type: 'text',
        label: 'Company',
        required: true,
        placeholder: 'e.g., Microsoft'
      },
      {
        name: 'role',
        type: 'text',
        label: 'Current Role',
        required: true,
        placeholder: 'e.g., Senior Software Engineer'
      },
      {
        name: 'start_date',
        type: 'date',
        label: 'Review Period Start',
        required: true
      },
      {
        name: 'end_date',
        type: 'date',
        label: 'Review Period End',
        required: true
      },
      {
        name: 'manager',
        type: 'text',
        label: 'Manager',
        required: false,
        placeholder: 'e.g., Jane Smith'
      }
    ],
    supportsRubric: true,
    defaultDurationDays: 365, // 1 year
    isActive: true, // Enable performance review focus
    quickTips: [
      'Document achievements throughout the review period',
      'Gather feedback from peers and stakeholders',
      'Set clear goals aligned with company objectives',
      'Track progress on competencies regularly'
    ]
  },
  
  // Future focus types (not active yet)
  research: {
    value: 'research',
    label: 'Research Project',
    description: 'Track your research progress, document findings, and prepare for publication',
    duration: '3-6 months',
    icon: '🔬',
    dashboardComponent: InternshipDashboard, // Placeholder, will create ResearchDashboard later
    setupFields: [
      {
        name: 'project_title',
        type: 'text',
        label: 'Project Title',
        required: true,
        placeholder: 'e.g., Machine Learning for Climate Prediction'
      },
      {
        name: 'institution',
        type: 'text',
        label: 'Institution/Lab',
        required: true,
        placeholder: 'e.g., MIT CSAIL'
      },
      {
        name: 'advisor',
        type: 'text',
        label: 'Advisor/PI',
        required: false,
        placeholder: 'e.g., Dr. Jane Smith'
      },
      {
        name: 'start_date',
        type: 'date',
        label: 'Start Date',
        required: true
      },
      {
        name: 'expected_end_date',
        type: 'date',
        label: 'Expected End Date',
        required: true
      }
    ],
    supportsRubric: false,
    defaultDurationDays: 180, // 6 months
    isActive: false,
    quickTips: []
  },
  
  new_job: {
    value: 'new_job',
    label: 'New Job (First 90 Days)',
    description: 'Navigate your first 90 days successfully with structured goals and milestones',
    duration: '90 days',
    icon: '💼',
    dashboardComponent: InternshipDashboard, // Placeholder
    setupFields: [
      {
        name: 'company',
        type: 'text',
        label: 'Company',
        required: true,
        placeholder: 'e.g., Amazon'
      },
      {
        name: 'role',
        type: 'text',
        label: 'Role',
        required: true,
        placeholder: 'e.g., Senior Software Engineer'
      },
      {
        name: 'start_date',
        type: 'date',
        label: 'Start Date',
        required: true
      },
      {
        name: 'manager',
        type: 'text',
        label: 'Manager Name',
        required: false,
        placeholder: 'e.g., John Doe'
      }
    ],
    supportsRubric: true,
    defaultDurationDays: 90,
    isActive: false,
    quickTips: []
  },
  
  side_project: {
    value: 'side_project',
    label: 'Side Project',
    description: 'Turn your side project into a success story with milestone tracking',
    duration: 'Flexible',
    icon: '🚀',
    dashboardComponent: InternshipDashboard, // Placeholder
    setupFields: [
      {
        name: 'project_name',
        type: 'text',
        label: 'Project Name',
        required: true,
        placeholder: 'e.g., TaskMaster Pro'
      },
      {
        name: 'project_type',
        type: 'select',
        label: 'Project Type',
        required: true,
        options: [
          { value: 'web_app', label: 'Web Application' },
          { value: 'mobile_app', label: 'Mobile App' },
          { value: 'open_source', label: 'Open Source Tool' },
          { value: 'content', label: 'Content/Course' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'start_date',
        type: 'date',
        label: 'Start Date',
        required: true
      },
      {
        name: 'target_launch_date',
        type: 'date',
        label: 'Target Launch Date',
        required: false
      }
    ],
    supportsRubric: false,
    defaultDurationDays: 90,
    isActive: false,
    quickTips: []
  },
  
  academic: {
    value: 'academic',
    label: 'Academic Semester',
    description: 'Excel in your coursework and research with structured tracking',
    duration: '1 semester',
    icon: '🎓',
    dashboardComponent: InternshipDashboard, // Placeholder
    setupFields: [
      {
        name: 'institution',
        type: 'text',
        label: 'Institution',
        required: true,
        placeholder: 'e.g., Stanford University'
      },
      {
        name: 'semester',
        type: 'select',
        label: 'Semester',
        required: true,
        options: [
          { value: 'fall', label: 'Fall' },
          { value: 'spring', label: 'Spring' },
          { value: 'summer', label: 'Summer' }
        ]
      },
      {
        name: 'year',
        type: 'text',
        label: 'Year',
        required: true,
        placeholder: 'e.g., 2024'
      },
      {
        name: 'major_courses',
        type: 'text_array',
        label: 'Major Courses',
        required: false,
        max: 6,
        placeholder: 'e.g., CS 161: Algorithms'
      }
    ],
    supportsRubric: false,
    defaultDurationDays: 120, // ~4 months
    isActive: false,
    quickTips: []
  }
};

// Helper functions
export const getActiveFocusTypes = () => {
  return Object.values(FOCUS_TYPES).filter(type => type.isActive);
};

export const getFocusTypeConfig = (focusType: string): FocusTypeConfig | null => {
  return FOCUS_TYPES[focusType] || null;
};

export const isValidFocusType = (focusType: string): boolean => {
  return focusType in FOCUS_TYPES;
};

export const getDefaultDuration = (focusType: string): number => {
  const config = getFocusTypeConfig(focusType);
  return config?.defaultDurationDays || 90;
};